import React, { createContext, useContext, useState, useEffect } from 'react';

const FlashcardContext = createContext();

export function useFlashcards() {
  return useContext(FlashcardContext);
}

export function FlashcardProvider({ children }) {
  // Flashcards state with local storage persistence
  const [flashcards, setFlashcards] = useState(() => {
    const savedCards = localStorage.getItem('flashcards');
    if (savedCards) {
      return JSON.parse(savedCards);
    }

    // Import default card collections
    const spanish = require('../data/spanish.json');
    const french = require('../data/french.json');
    const science = require('../data/science.json');
    const history = require('../data/history.json');
    const harryPotter = require('../data/harrypotter.json');
    const mandarin = require('../data/mandarin.json');
    const hindi = require('../data/hindi.json');
    const geography = require('../data/geography.json');
    const math = require('../data/math.json');
    const math1 = require('../data/math1.json');
    const math2 = require('../data/math2.json');
    const math3 = require('../data/math3.json');
    const math4 = require('../data/math4.json');
    const math5 = require('../data/math5.json');

    // Default study decks with difficulty levels
    const defaultDecks = [
      // Core subjects
      science,
      history,
      geography,
      // Languages
      spanish,
      french,
      mandarin,
      hindi,
      // Special subjects
      harryPotter,
      // Math at the end
      math,
      math1,
      math2,
      math3,
      math4,
      math5
    ];

    // Create a Map to store unique topic-difficulty combinations
    const groupMap = new Map();
    const allCards = [];
    const allGroups = [];

    // Process each deck
    defaultDecks.forEach(deck => {
      if (Array.isArray(deck.cards)) {
        deck.cards.forEach(difficultyGroup => {
          if (difficultyGroup && Array.isArray(difficultyGroup.cards) && difficultyGroup.cards.length > 0) {
            // Create a unique key for this topic-difficulty combination
            const key = `${deck.topic}-${difficultyGroup.difficulty}`;
            
            // Only process this combination if we haven't seen it before
            if (!groupMap.has(key)) {
              const groupId = `group-${key}-${Date.now()}`;
              const cardIds = [];
              
              // Process cards for this group
              difficultyGroup.cards.forEach(card => {
                if (card && card.question && card.answer && card.incorrect) {
                  const cardId = `card-${Date.now()}-${Math.random()}`;
                  cardIds.push(cardId);
                  allCards.push({
                    id: cardId,
                    question: card.question,
                    answer: card.answer,
                    incorrect: card.incorrect,
                    groupId: groupId,
                    difficulty: difficultyGroup.difficulty,
                    createdAt: new Date().toISOString(),
                    reviewed: false,
                    lastReviewed: null
                  });
                }
              });

              // Only create the group if we have valid cards
              if (cardIds.length > 0) {
                // Process topic name
                let displayName;
                if (deck.topic.toLowerCase() === "fiction") {
                    displayName = "Harry Potter";
                } else if (deck.topic.toLowerCase().startsWith("mathematics grade")) {
                    displayName = deck.topic.replace("Mathematics Grade", "Math");
                } else if (deck.topic.toLowerCase() === "language") {
                    // Use the file name for languages
                    const fileName = deck.topic;
                    displayName = fileName.charAt(0).toUpperCase() + fileName.slice(1);
                } else {
                    // Capitalize first letter of other topics
                    displayName = deck.topic.charAt(0).toUpperCase() + deck.topic.slice(1);
                }
                
                const group = {
                  id: groupId,
                  name: `${displayName} - ${difficultyGroup.difficulty}`,
                  topic: displayName,
                  difficulty: difficultyGroup.difficulty,
                  createdAt: new Date().toISOString(),
                  cardIds: cardIds
                };
                
                allGroups.push(group);
                groupMap.set(key, group);
              }
            }
          }
        });
      }
    });

    // Sort groups by topic name and difficulty
    allGroups.sort((a, b) => {
      // Extract numbers from Math topics (Math1, Math2, etc.)
      const mathA = a.topic.startsWith('Math');
      const mathB = b.topic.startsWith('Math');
      
      // Put Math topics at the end
      if (mathA && !mathB) return 1;
      if (!mathA && mathB) return -1;
      
      // If both are math topics, sort by number then difficulty
      if (mathA && mathB) {
        const numA = parseInt(a.topic.match(/\d+/)?.[0] || '0');
        const numB = parseInt(b.topic.match(/\d+/)?.[0] || '0');
        if (numA !== numB) return numA - numB;
      }
      
      // For all other cases, sort alphabetically then by difficulty
      const topicCompare = a.topic.localeCompare(b.topic);
      if (topicCompare !== 0) return topicCompare;
      
      const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
      return (difficultyOrder[a.difficulty] || 99) - (difficultyOrder[b.difficulty] || 99);
    });
    
    // Save groups to localStorage
    localStorage.setItem('groups', JSON.stringify(allGroups));
    
    // Return all created cards
    return allCards;
  });

  // Groups state for organizing flashcards
  const [groups, setGroups] = useState(() => {
    const savedGroups = localStorage.getItem('groups');
    return savedGroups ? JSON.parse(savedGroups) : [];
  });

  // Active group tracking
  const [activeGroupId, setActiveGroupId] = useState(() => {
    const savedGroupId = localStorage.getItem('activeGroupId');
    return savedGroupId ? JSON.parse(savedGroupId) : null;
  });

  // Score tracking with improved format
  const [score, setScore] = useState(() => {
    try {
      const savedScore = localStorage.getItem('score');
      if (savedScore) {
        const parsedScore = JSON.parse(savedScore);
        // Handle migration from old format
        if (typeof parsedScore === 'number') {
          return {
            total: parsedScore,
            right: Math.max(0, parsedScore),
            wrong: Math.max(0, -parsedScore)
          };
        }
        return parsedScore;
      }
    } catch (e) {
      console.warn('Error loading score:', e);
    }
    return {
      total: 0,
      right: 0,
      wrong: 0
    };
  });

  // Sound preferences
  const [isMuted, setIsMuted] = useState(() => {
    const savedMuted = localStorage.getItem('isMuted');
    return savedMuted ? JSON.parse(savedMuted) : false;
  });

  // Persistence handlers
  useEffect(() => {
    localStorage.setItem('flashcards', JSON.stringify(flashcards));
  }, [flashcards]);

  useEffect(() => {
    localStorage.setItem('groups', JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    localStorage.setItem('activeGroupId', JSON.stringify(activeGroupId));
  }, [activeGroupId]);

  useEffect(() => {
    localStorage.setItem('score', JSON.stringify(score));
  }, [score]);

  useEffect(() => {
    localStorage.setItem('isMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  // Flashcard Management
  const addFlashcard = (question, answer, groupId = null, incorrect = null) => {
    const newCard = {
      id: Date.now(),
      question,
      answer,
      incorrect,
      groupId,
      createdAt: new Date().toISOString(),
      reviewed: false,
      lastReviewed: null
    };
    setFlashcards(prev => [...prev, newCard]);
    return newCard;
  };

  const markCardAsReviewed = (id) => {
    setFlashcards(prev => prev.map(card => 
      card.id === id ? { 
        ...card, 
        reviewed: true, 
        lastReviewed: new Date().toISOString() 
      } : card
    ));
  };

  const deleteFlashcard = (id) => {
    const card = flashcards.find(c => c.id === id);
    if (card && card.groupId) {
      // Update group's cardIds when deleting a card
      setGroups(prev =>
        prev.map(group =>
          group.id === card.groupId
            ? { ...group, cardIds: group.cardIds.filter(cardId => cardId !== id) }
            : group
        )
      );
    }
    setFlashcards(prev => prev.filter(card => card.id !== id));
  };

  // Group Management
  const createGroup = (name, topic, difficulty) => {
    const newGroup = {
      id: Date.now().toString(),
      name,
      topic,
      difficulty,
      createdAt: new Date().toISOString(),
      cardIds: []
    };
    setGroups(prev => [...prev, newGroup]);
    return newGroup.id;
  };

  const deleteGroup = (groupId) => {
    // Remove group assignment from cards
    setFlashcards(prev => 
      prev.map(card => 
        card.groupId === groupId ? { ...card, groupId: null } : card
      )
    );
    // Remove the group
    setGroups(prev => prev.filter(group => group.id !== groupId));
    // Clear active group if it was deleted
    if (activeGroupId === groupId) {
      setActiveGroupId(null);
    }
  };

  // Bulk card addition to group
  const addFlashcardsToGroup = (cards, groupId) => {
    const newCards = cards.map(card => ({
      id: Date.now() + Math.random(),
      question: card.question,
      answer: card.answer,
      incorrect: card.incorrect,
      groupId,
      createdAt: new Date().toISOString()
    }));

    setFlashcards(prev => [...prev, ...newCards]);
    setGroups(prev =>
      prev.map(group =>
        group.id === groupId
          ? { ...group, cardIds: [...group.cardIds, ...newCards.map(card => card.id)] }
          : group
      )
    );

    return newCards;
  };

    // Score Management
  const updateScore = (correct, cardId) => {
    setScore(prev => ({
      total: prev.total + (correct ? 1 : -1),
      right: prev.right + (correct ? 1 : 0),
      wrong: prev.wrong + (correct ? 0 : 1)
    }));
    markCardAsReviewed(cardId);
  };

  // Sound Management
  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  // Get cards from active group
  const getVisibleFlashcards = () => {
    return flashcards.filter(card => card.groupId === activeGroupId);
  };

  // Sort groups with non-Math subjects first, then Math topics in order
  const sortedGroups = [...groups].sort((a, b) => {
    // Extract numbers from Math topics (Math1, Math2, etc.)
    const mathA = a.topic.startsWith('Math');
    const mathB = b.topic.startsWith('Math');
    
    // Put Math topics at the end
    if (mathA && !mathB) return 1;
    if (!mathA && mathB) return -1;
    
    // If both are math topics, sort by number then difficulty
    if (mathA && mathB) {
      const numA = parseInt(a.topic.match(/\d+/)?.[0] || '0');
      const numB = parseInt(b.topic.match(/\d+/)?.[0] || '0');
      if (numA !== numB) return numA - numB;
      
      const diffOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
      return (diffOrder[a.difficulty] || 99) - (diffOrder[b.difficulty] || 99);
    }
    
    // For other topics, sort alphabetically then by difficulty
    const topicCompare = a.topic.localeCompare(b.topic);
    if (topicCompare !== 0) return topicCompare;
    
    const diffOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
    return (diffOrder[a.difficulty] || 99) - (diffOrder[b.difficulty] || 99);
  });

  const value = {
    flashcards: getVisibleFlashcards(),
    allFlashcards: flashcards,
    groups: sortedGroups,
    activeGroupId,
    addFlashcard,
    deleteFlashcard,
    createGroup,
    deleteGroup,
    addFlashcardsToGroup,
    setActiveGroupId,
    score,
    updateScore,
    isMuted,
    toggleMute,
    markCardAsReviewed
  };

  return (
    <FlashcardContext.Provider value={value}>
      {children}
    </FlashcardContext.Provider>
  );
}
